import React, { useState, useEffect, useContext } from 'react';
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Edit, Plus, Users, Clock, ChevronLeft, ChevronRight, FileText, Info, ChevronDown, ChevronUp, ListFilter, ArrowUp, ArrowDown, Search, Trash2 } from "lucide-react";
import axios from 'axios';
import WorkPermitCreation from './WPCreateOrderVestas';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, "")}/api`;

const ITEMS_PER_PAGE = 10;

const UploadExcelModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const { user } = useContext(UserContext);
  const [file, setFile] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = user?.accessToken || localStorage.getItem("access_token");
        const response = await axios.get(`${API_BASE_URL}/company/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompanies(response.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setErrorMessage(error);
      }
    };

    fetchCompanies();
  }, [user]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setFile(selectedFile);
        setErrorMessage("");
      } else {
        setErrorMessage("Invalid file type. Please select an Excel file (.xlsx or .xls).");
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedCompany) {
      setErrorMessage("Please select a file and a company");
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("company_to", selectedCompany);

      const response = await axios.post(`${API_BASE_URL}/upload-excel/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201 || response.status === 207) {
        onUploadSuccess();
        onClose();
      } else {
        setErrorMessage(response.data.error || "Failed to upload");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMessage("Failed to upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Upload Excel File</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <label className="text-sm font-medium">Select Company</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full mt-1 p-2 border rounded"
            >
              <option value="">Select a company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Select Excel File</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="w-full p-2 border rounded mt-1"
              disabled={uploading}
            />
          </div>
          {errorMessage && <div className="text-red-600 text-sm">{errorMessage}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={uploading || !file || !selectedCompany}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WorkPermit = () => {
  const { user, loading: contextLoading } = useContext(UserContext);
  const [teams, setTeams] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preselectedDate, setPreselectedDate] = useState(null);
  const [preselectedTeam, setPreselectedTeam] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "order_number",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = user?.accessToken || localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found");

      const config = {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      };

      const [ordersResponse, teamsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/workpermit/`, config),
        axios.get(`${API_BASE_URL}/teams/`, config),
        axios.get(`${API_BASE_URL}/users/`, config),
      ]);

      setWorkOrders(ordersResponse.data || []);
      setCompanyUsers(usersResponse.data?.filter((user) => user.is_active && !user.is_company_admin) || []);
      setTeams(teamsResponse.data?.map((team) => ({ ...team, expanded: false })) || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!contextLoading && !user?.accessToken) {
      window.location.href = "/login";
      return;
    }
    if (!contextLoading) fetchCompanyData();
  }, [contextLoading, user]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" strokeWidth={1.5} />
    );
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }

    const token = localStorage.getItem("access_token");
    if (!token) {
      alert("No access token found. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/workpermit/${orderId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchCompanyData();
      setSelectedOrder(null);
      console.log(`Order ${orderId} deleted successfully`);
    } catch (error) {
      console.error("Error deleting order:", error);
      if (error.response?.status === 401) {
        alert("Unauthorized. Please log in again.");
      } else {
        alert("Failed to delete order. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getSortedAndFilteredOrders = () => {
    let filteredOrders = (workOrders || []).filter((order) => {
      if (!order) return false;
      const searchFields = [
        order.order_number,
        order.work_permit_number,
        order.turbine_data?.location,
        order.turbine_data?.turbine_number,
        order.turbine_data?.wind_farm_data?.name,
        order.work_scope_data?.name,
        order.work_scope_real,
        order.turbine_data?.model_data?.name,
      ].map((field) => String(field || "").toLowerCase());
      return searchFields.some((field) => field.includes(searchTerm.toLowerCase()));
    });

    return filteredOrders.sort((a, b) => {
      if (!a || !b) return 0;
      let aValue =
        sortConfig.key === "windfarm"
          ? a.turbine_data?.wind_farm_data?.name
          : sortConfig.key === "location"
            ? a.turbine_data?.location
            : sortConfig.key === "turbine_number"
              ? a.turbine_data?.turbine_number
              : sortConfig.key === "name"
                ? a.work_scope_data?.name
                : a[sortConfig.key] || "";
      let bValue =
        sortConfig.key === "windfarm"
          ? b.turbine_data?.wind_farm_data?.name
          : sortConfig.key === "location"
            ? b.turbine_data?.location
            : sortConfig.key === "turbine_number"
              ? b.turbine_data?.turbine_number
              : sortConfig.key === "name"
                ? b.work_scope_data?.name
                : b[sortConfig.key] || "";
      aValue = String(aValue || "").toLowerCase();
      bValue = String(bValue || "").toLowerCase();
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const sortedAndFilteredOrders = getSortedAndFilteredOrders();
  const totalPages = Math.ceil(sortedAndFilteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = sortedAndFilteredOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (contextLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>{error}</p>
        <Button onClick={fetchCompanyData} className="ml-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex-1 flex flex-col overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-4 text-2xl font-semibold text-[#002D43]">Orders List</h2>
          <div className="flex space-x-4 mr-4">
            <Button
              onClick={() => {
                setPreselectedDate(null);
                setPreselectedTeam(null);
                setModalMode("create");
                setSelectedOrder(null);
                setShowOrderModal(true);
              }}
              className="bg-white px-4 py-2 rounded-full hover:bg-gray-100 transition-colors duration-300 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Order
            </Button>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-white px-4 py-2 rounded-full hover:bg-gray-100 transition-colors duration-300 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload Excel
            </Button>
          </div>
        </div>
        <div className="p-4 bg-white rounded-2xl shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex items-center flex-1">
              <div className="relative flex-1">
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by order number, work permit number, location, turbine, windfarm..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 px-3 py-2"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="relative overflow-x-auto">
            <div className="h-[calc(100vh-240px)] overflow-y-auto rounded-lg scrollbar-gutter-stable w-full">
              <div className="grid grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_minmax(150px,1fr)_minmax(120px,1fr)_minmax(150px,1fr)_minmax(120px,max-content)] w-full">
                <div className="grid-table-header contents">
                  <div
                    className="sticky top-0 bg-white z-10 cursor-pointer border-b border-gray-200"
                    onClick={() => handleSort("order_number")}
                  >
                    <div className="flex items-center gap-2 p-2 font-semibold">
                      Order Number
                      {renderSortIndicator("order_number")}
                    </div>
                  </div>
                  <div
                    className="sticky top-0 bg-white z-10 cursor-pointer border-b border-gray-200"
                    onClick={() => handleSort("work_permit_number")}
                  >
                    <div className="flex items-center gap-2 p-2 font-semibold">
                      Work Permit Number
                      {renderSortIndicator("work_permit_number")}
                    </div>
                  </div>
                  <div
                    className="sticky top-0 bg-white z-10 cursor-pointer border-b border-gray-200"
                    onClick={() => handleSort("location")}
                  >
                    <div className="flex items-center gap-2 p-2 font-semibold">
                      Location
                      {renderSortIndicator("location")}
                    </div>
                  </div>
                  <div
                    className="sticky top-0 bg-white z-10 cursor-pointer border-b border-gray-200"
                    onClick={() => handleSort("turbine_number")}
                  >
                    <div className="flex items-center gap-2 p-2 font-semibold">
                      Turbine
                      {renderSortIndicator("turbine_number")}
                    </div>
                  </div>
                  <div
                    className="sticky top-0 bg-white z-10 cursor-pointer border-b border-gray-200"
                    onClick={() => handleSort("windfarm")}
                  >
                    <div className="flex items-center gap-2 p-2 font-semibold">
                      Windfarm
                      {renderSortIndicator("windfarm")}
                    </div>
                  </div>
                  <div
                    className="sticky top-0 bg-white z-10 cursor-pointer border-b border-gray-200"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-2 p-2 font-semibold">
                      Work Scope
                      {renderSortIndicator("name")}
                    </div>
                  </div>
                  <div className="sticky top-0 bg-white z-10 text-right border-b border-gray-200">
                    <div className="p-2 font-semibold">Actions</div>
                  </div>
                </div>
                {loading ? (
                  <div className="grid-table-row contents">
                    <div className="col-span-7 text-center py-2">Loading...</div>
                  </div>
                ) : paginatedOrders.length > 0 ? (
                  paginatedOrders.map((order, index) => (
                    <div
                      key={order?.id || index}
                      className={`grid-table-row contents hover:bg-gray-50 cursor-pointer ${selectedOrder && selectedOrder.id === order.id ? "bg-blue-100" : ""}`}
                      onClick={() => setSelectedOrder(order)}
                      onDoubleClick={() => {
                        setSelectedOrder(order);
                        setModalMode("edit");
                        setShowOrderModal(true);
                      }}
                    >
                      <div className="border-b border-gray-200">
                        <div className="flex items-center p-2">
                          <div className="text-sm font-medium text-[#002D43] truncate">
                            {order?.order_number || "-"}
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-gray-200">
                        <div className="flex items-center p-2">
                          <div className="text-sm font-medium text-[#002D43] truncate">
                            {order?.work_permit_number || "-"}
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-gray-200">
                        <div className="text-sm text-gray-600 p-2">
                          {order?.turbine_data?.location || "-"}
                        </div>
                      </div>
                      <div className="border-b border-gray-200">
                        <div className="text-sm text-gray-600 p-2">
                          {order?.turbine_data?.turbine_number || "-"} /{" "}
                          {order?.turbine_data?.model_data?.name || "-"}
                        </div>
                      </div>
                      <div className="border-b border-gray-200">
                        <div className="text-sm text-gray-600 p-2">
                          {order?.turbine_data?.wind_farm_data?.name || "-"}
                        </div>
                      </div>
                      <div className="border-b border-gray-200">
                        <div className="text-sm text-gray-600 p-2">
                          {order?.work_scope_data?.name === "Other"
                            ? `${order.work_scope_real} (undefined)`
                            : order?.work_scope_data?.name || "-"}
                        </div>
                      </div>
                      <div className="text-right border-b border-gray-200">
                        <div className="flex justify-end space-x-2 p-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrder(order);
                              setModalMode("edit");
                              setShowOrderModal(true);
                            }}
                            className="p-1.5 bg-gray-100 text-[#002D43] rounded-full hover:bg-gray-200 transition-colors"
                            title="Edit Order"
                            disabled={loading}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(order.id);
                            }}
                            className="p-1.5 bg-gray-100 text-red-600 rounded-full hover:bg-gray-200 transition-colors"
                            title="Delete Order"
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="grid-table-row contents">
                    <div className="col-span-7 text-center py-2">No orders found.</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-[#344054]">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
              {Math.min(currentPage * ITEMS_PER_PAGE, sortedAndFilteredOrders.length)} of{" "}
              {sortedAndFilteredOrders.length} items
            </div>
            <div className="flex space-x-2">
              {currentPage > 1 && (
                <button
                  className="px-4 py-2 border border-[#D0D5DD] rounded-full bg-white text-[#344054] hover:bg-gray-50 flex items-center justify-center transition-colors duration-200"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
              )}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 border rounded-full ${page === currentPage
                      ? "bg-[#E5F7FF] text-black border-[#D0D5DD]"
                      : "bg-white text-[#344054] border-[#D0D5DD] hover:bg-gray-50"
                    } flex items-center justify-center transition-colors duration-200`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  className="px-4 py-2 border border-[#D0D5DD] rounded-full bg-white text-[#344054] hover:bg-gray-50 flex items-center justify-center transition-colors duration-200"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <WorkPermitCreation
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
          setModalMode("create");
        }}
        refreshPreOrders={fetchCompanyData}
        mode={modalMode}
        order={selectedOrder}
      />

      <UploadExcelModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={fetchCompanyData}
      />
    </div>
  );
};

export default WorkPermit;